/**
 * Kontextové menu pro písničku v repertoáru — rychlá editace bez modalu.
 *
 * Otevři: openSongContextMenu(x, y, songId, onChanged)
 * Po každé změně se uloží do DB; onChanged() se zavolá až při zavření menu
 * (aby drobné re-rendery nezasahovaly do hladké navigace v menu).
 */
import { supabase } from '../lib/supabase.js';
import { openSongEditModal } from './song-edit-modal.js';

const MICHAL_INSTRUMENTS = ['kytara', 'key', 'bicí'];
const MATOUS_INSTRUMENTS = ['aku', 'tenor', 'alt', 'sopran'];
const SINGERS = [
    { value: 'misa',   label: 'Míša',   bg: '#fff4c2', border: '#f0d770' },
    { value: 'matous', label: 'Matouš', bg: '#d4f0d0', border: '#8fc788' },
    { value: 'michal', label: 'Michal', bg: '#d6e4f7', border: '#7da7d9' }
];

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export async function openSongContextMenu(x, y, songId, onChanged) {
    closeContextMenu();

    const { data: song, error } = await supabase
        .from('songs').select('*').eq('id', songId).single();
    if (error) {
        alert('Načtení písničky selhalo: ' + error.message);
        return;
    }

    const ins = { ...(song.instruments && typeof song.instruments === 'object' ? song.instruments : {}) };
    // Zpěváci jsou pole: 1 = sólo, 2 = duet, 3 = trio.
    // Fallback na starý 1-string `singer` kvůli starým datům.
    const initialSingers = Array.isArray(song.singers) && song.singers.length
        ? song.singers.filter(Boolean)
        : (song.singer ? [song.singer] : []);
    const current = {
        michal: ins.michal || '',
        matous: ins.matous || '',
        drum:   ins.drum === true,
        singers: new Set(initialSingers)
    };
    // Optimisticky aktualizujeme DOM přímo (rychlé), bez plného re-renderu.
    // Najdeme všechny řádky s touto písní v playlistu (může být víckrát).
    const rowsForSong = () => document.querySelectorAll(`.rep-song[data-song-id="${cssEscape(songId)}"]`);

    const menu = document.createElement('div');
    menu.className = 'rep-context-menu';
    menu.id = 'rep-context-menu';
    menu.innerHTML = `
        <div class="rcm-head">
            ${song.song_number != null ? `<span class="rcm-num">#${song.song_number}</span>` : ''}
            <span class="rcm-title">${esc(song.title || '')}</span>
        </div>

        <div class="rcm-section">
            <div class="rcm-label">Michal hraje</div>
            <div class="rcm-options" data-group="michal">
                ${MICHAL_INSTRUMENTS.map((v) => optBtn(v, current.michal === v, 'michal', v)).join('')}
                ${optBtn('—', !current.michal, 'michal', '', 'rcm-clear')}
            </div>
        </div>

        <div class="rcm-section">
            <div class="rcm-label">Matouš hraje</div>
            <div class="rcm-options" data-group="matous">
                ${MATOUS_INSTRUMENTS.map((v) => optBtn(v, current.matous === v, 'matous', v)).join('')}
                ${optBtn('—', !current.matous, 'matous', '', 'rcm-clear')}
            </div>
        </div>

        <div class="rcm-section">
            <label class="rcm-check">
                <input type="checkbox" data-toggle-drum ${current.drum ? 'checked' : ''}>
                <span>Akustický DRUM podklad</span>
            </label>
        </div>

        <div class="rcm-section">
            <div class="rcm-label">Zpívá <span class="rcm-sublabel">(klikni víc pro duet)</span></div>
            <div class="rcm-options" data-group="singer">
                ${SINGERS.map((s) => `
                    <button class="rcm-opt rcm-singer${current.singers.has(s.value) ? ' active' : ''}"
                            data-set-singer="${s.value}"
                            style="background:${s.bg}; border-color:${s.border};">${esc(s.label)}</button>
                `).join('')}
                <button class="rcm-opt rcm-clear${current.singers.size === 0 ? ' active' : ''}" data-set-singer="">—</button>
            </div>
        </div>

        <div class="rcm-section rcm-footer">
            <button class="rcm-detail" data-action="detail">⚙ Upravit detailně…</button>
        </div>
    `;
    document.body.appendChild(menu);
    positionMenu(menu, x, y);

    // Klik na možnost = okamžitě aplikuj a zavři menu.
    menu.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.stopPropagation();

        if (btn.dataset.action === 'detail') {
            closeContextMenu();
            openSongEditModal(songId, onChanged);
            return;
        }

        // Instrument (michal / matous)
        if (btn.dataset.set) {
            const key = btn.dataset.set;
            const value = btn.dataset.value;
            const newIns = { ...ins };
            if (value) newIns[key] = value;
            else delete newIns[key];
            updateRowInstruments(rowsForSong(), newIns);
            closeContextMenu();
            supabase.from('songs').update({ instruments: newIns }).eq('id', songId).then(({ error: e2 }) => {
                if (e2) alert('Uložení selhalo: ' + e2.message);
            });
            return;
        }

        // Zpěvák — multi-select. Klik na "—" smaže všechny, jinak toggle.
        // Menu se nezavírá, aby šel přidat 2. zpěvák pro duet.
        if (btn.dataset.setSinger !== undefined) {
            const v = btn.dataset.setSinger;
            if (v === '') {
                current.singers.clear();
            } else if (current.singers.has(v)) {
                current.singers.delete(v);
            } else {
                current.singers.add(v);
            }
            const arr = [...current.singers].sort();
            // Vizuálně přepni aktivní chipy v menu
            menu.querySelectorAll('[data-set-singer]').forEach((el) => {
                const elVal = el.dataset.setSinger;
                if (elVal === '') el.classList.toggle('active', arr.length === 0);
                else el.classList.toggle('active', current.singers.has(elVal));
            });
            // Hned přemaluj řádek v plánovači
            updateRowSinger(rowsForSong(), arr);
            // Ulož na pozadí (singer = první pro zpětnou kompat, singers = celé pole)
            const payload = { singers: arr, singer: arr[0] || null };
            supabase.from('songs').update(payload).eq('id', songId).then(({ error: e3 }) => {
                if (e3) alert('Uložení selhalo: ' + e3.message);
            });
            return;
        }
    });

    // DRUM toggle = stejně rychle se zavře.
    menu.querySelector('[data-toggle-drum]').addEventListener('change', (e) => {
        const checked = e.target.checked;
        const newIns = { ...ins };
        if (checked) newIns.drum = true; else delete newIns.drum;
        updateRowInstruments(rowsForSong(), newIns);
        closeContextMenu();
        supabase.from('songs').update({ instruments: newIns }).eq('id', songId).then(({ error: e4 }) => {
            if (e4) alert('Uložení selhalo: ' + e4.message);
        });
    });

    // Outside mousedown zavře menu (capture, aby předběhlo marquee/drag)
    const onOutside = (ev) => {
        if (!menu.contains(ev.target)) {
            ev.preventDefault();
            ev.stopPropagation();
            closeContextMenu();
        }
    };
    const onKey = (ev) => {
        if (ev.key === 'Escape') {
            ev.preventDefault();
            closeContextMenu();
        }
    };
    setTimeout(() => {
        document.addEventListener('mousedown', onOutside, true);
        document.addEventListener('keydown', onKey, true);
    }, 0);

    menu._cleanup = () => {
        document.removeEventListener('mousedown', onOutside, true);
        document.removeEventListener('keydown', onKey, true);
    };
}

// Bezpečné escapování ID pro selektor (UUID je bezpečné, ale jistota nikdy neuškodí)
function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/(["\\])/g, '\\$1');
}

// Update barvy řádku podle zpěváků (pole; 0 = bez barvy, 1 = sólo, 2+ = gradient z CSS)
function updateRowSinger(rowEls, singers) {
    const arr = Array.isArray(singers) ? [...singers].sort() : (singers ? [singers] : []);
    rowEls.forEach((row) => {
        if (arr.length) row.setAttribute('data-singer', arr.join(','));
        else row.removeAttribute('data-singer');
    });
}

// Update textu nástrojů v řádku — generuje stejné HTML jako repertoir.js formatInstruments
function updateRowInstruments(rowEls, instruments) {
    const html = formatInstrumentsForRow(instruments);
    rowEls.forEach((row) => {
        const insEl = row.querySelector('.rep-ins');
        if (insEl) insEl.innerHTML = html;
    });
}

function escAttr(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Lokální kopie format funkce z repertoir.js (musí být v shodě s ní).
const ROW_LABELS = { michal: 'M', matous: 'Mt', misa: 'Mš', tata: 'T' };
function formatInstrumentsForRow(obj) {
    if (!obj || typeof obj !== 'object') return '';
    const parts = [];
    const order = ['michal', 'matous', 'misa', 'tata'];
    const present = order.filter((k) => obj[k]);
    for (const k of present) {
        const v = String(obj[k]).trim();
        if (!v) continue;
        const label = present.length > 1 ? `${ROW_LABELS[k]}: ${escAttr(v)}` : escAttr(v);
        parts.push(`<span class="ins-tok ins-${k}" data-instr="${escAttr(v)}">${label}</span>`);
    }
    for (const k of Object.keys(obj)) {
        if (order.includes(k) || k === 'drum') continue;
        const v = obj[k];
        if (!v) continue;
        parts.push(`<span class="ins-tok">${escAttr(String(v))}</span>`);
    }
    if (obj.drum === true || String(obj.drum).toUpperCase() === 'DRUM') {
        parts.push('<span class="ins-tok ins-drum">DRUM</span>');
    }
    return parts.join('<span class="ins-sep"> · </span>');
}

function optBtn(label, isActive, group, value, extra = '') {
    return `<button class="rcm-opt ${extra}${isActive ? ' active' : ''}"
                    data-set="${esc(group)}" data-value="${esc(value)}">${esc(label)}</button>`;
}

function updateGroup(menu, group, currentValue) {
    const sel = group === 'singer' ? '[data-set-singer]' : `[data-set="${group}"]`;
    menu.querySelectorAll(sel).forEach((el) => {
        const v = group === 'singer' ? el.dataset.setSinger : el.dataset.value;
        el.classList.toggle('active', v === currentValue);
    });
}

function positionMenu(menu, x, y) {
    const rect = menu.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width - 8;
    const maxY = window.innerHeight - rect.height - 8;
    menu.style.left = Math.max(4, Math.min(x, maxX)) + 'px';
    menu.style.top = Math.max(4, Math.min(y, maxY)) + 'px';
}

export function closeContextMenu() {
    const m = document.getElementById('rep-context-menu');
    if (m) {
        m._cleanup?.();
        m.remove();
    }
}
